// IE Hub - DebtCategory Component (Refactored)
// Modern debt category manager with lender intelligence, duplicate guards, and payment offer support.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Stack,
  Chip,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@/ui';
import Select from '@/ui/fields/Select';
import FreeSelect from '@/ui/fields/FreeSelect';
import TextWithTooltip from '@/components/TextWithTooltip';
import SelectToggle from '@/components/SelectToggle';
import CollapsibleTextArea from '@/components/CollapsibleTextArea';
import QuestionGroupForDialog from '@/components/QuestionGroupForDialog';
import {
  AddIcon,
  EditIcon,
  DeleteIcon,
  ExpandMoreIcon,
  ExpandLessIcon,
  CloseIcon,
} from '@/ui';
import { useReferenceIdHelp } from '@/components/referenceId';
import { UI_MESSAGES } from '@/constants/ui';
import { CARD_DEBT_TYPES, LOAN_DEBT_TYPES, OTHER_NON_PRIORITY_DEBT } from '@/constants/debtTypes';
import { useAlert } from '@/app/providers/AlertProvider';
import type {
  DebtCategoryKey,
  DebtFormEntry,
  DebtRepaymentFrequencyUi,
  EnergyCompanyRecord,
} from '@/shared/api/types';
import type { CustomerDebtRecord, DebtUpsertRequest, PaymentOfferUpsertRequest } from '@/shared/api/types';
import { DebtsService } from '@/shared/api/services/debtsService';
import { SharingOrchestrator } from '@/shared/api/services/sharingOrchestrator';
import { mapDebtFrequencyUiToApi } from '@/shared/lib/budgetFieldMapping';
import { getCustomerId } from '@/shared/lib/utils/getCustomerId';
import { useAuth } from '../shared/state/store';
import type {
  AccountTypeRecord,
  LenderParentRecord,
} from '@/shared/api/services/lenderService';
import { buildLenderLookup as buildArrearsLenderLookup, computeSelectionContext } from '@/shared/arrears/companyRules';
import { HIDDEN_COMPANY_NAMES } from '@/pages/private/noNavButFooter/UpdateBudgetPageCom/constants';

interface DebtCategoryProps {
  label: string;
  category: DebtCategoryKey;
  value: DebtFormEntry[];
  onChange: (next: DebtFormEntry[]) => void;
  /**
   * Normalized list of creditor/company names that already exist
   * across ALL debt sections on the Debts screen (credit cards, loans, overdrafts, others, arrears).
   * Used only for a soft guard dialog when user selects a company that already exists.
   */
  existingCreditorNames?: string[];
  /**
   * Complete list of existing debts across ALL categories on the Debts screen.
   * Used for hard duplicate checking on Save (same company + same reference number).
   */
  allExistingDebts?: DebtFormEntry[];
  placeholder?: string;
  lenders?: LenderParentRecord[];
  accountTypes?: AccountTypeRecord[];
  energyCompanies?: EnergyCompanyRecord[]; // optional list from ie-energy-companies-mstr (used for electricity arrears)
  readOnly?: boolean;
  loading?: boolean;
  description?: string;
  onDuplicateDetected?: (entry: DebtFormEntry) => void;
  availableBudget?: number; // optional budget for payment-offer guard
  /** When true, hides the Payment Offer panel in the dialog (e.g. UW flow where this is not required). */
  hidePaymentOffer?: boolean;
  hideAccountType?: boolean; // hide account type select for this usage
  density?: 'comfortable' | 'compact'; // compact reduces vertical spacing in dialog
  forceShowAccountType?: boolean; // force show the account type dropdown regardless of context rules
  /**
   * When true (and category is 'creditCards'), replaces the locked read-only
   * "Credit card" debt type with a selectable dropdown of card debt types
   * (Credit card / Store card / Charge card). Used by the UW budget flow only.
   */
  cardDebtTypeSelectable?: boolean;
  /**
   * When true (and category is 'loans'), replaces the generic account-type
   * dropdown with a restricted list of loan debt types (BNPL / Personal /
   * Employer loan / Secured Loan / Mortgage / Motor Finance / Private Student
   * Loan). Used by the UW budget flow only.
   */
  loanDebtTypeSelectable?: boolean;
  /**
   * When true (and category is 'otherDebts'), hides the generic debt-type
   * dropdown and records every entry under the fixed "Other Non Priority Debt"
   * account type (resolved from the account-type master). Notes also become
   * mandatory in this mode. Used by the UW budget flow only.
   */
  otherDebtTypeDefault?: boolean;
  /** Pre-resolved customerId — when provided, skips the async getCustomerId() lookup. */
  customerId?: string | number | null | undefined;
  /** Overrides the empty-state add button text. Defaults to "Add debts" (production wording). */
  addButtonLabel?: string;
  /** Overrides the "add another" button text. Defaults to "Add another debt" (production wording). */
  addAnotherButtonLabel?: string;
}

type DraftDebtEntry = DebtFormEntry;

const REPAYMENT_OPTIONS: { value: DebtRepaymentFrequencyUi; label: string }[] = [
  { value: 'weekly', label: 'Weekly' },
  { value: '4weekly', label: '4-Weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
  { value: 'yearly', label: 'Yearly' },
];

const DEFAULT_COMPANY_OPTION = '';
const MANUAL_COMPANY_OPTION = '__manual_company__';

// Locked account type codes for specific categories (legacy parity)
const LOCKED_CATEGORY_TYPES: Partial<Record<DebtCategoryKey, { code: string; label: string }>> = {
  creditCards: { code: 'ZC', label: 'Credit card' },
  overdrafts: { code: 'OD', label: 'Overdraft' },
};

const normalize = (value: string | null | undefined): string => (value ?? '').trim().toLowerCase();


type DebtFormErrors = Partial<{
  company: string;
  manualCompany: string;
  product: string;
  creditorName: string;
  accountNumber: string;
  accTypeCode: string;
  repaymentFrequency: string;
  paymentOfferAmount: string;
  notes: string;
}>;

const formatCurrency = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '£0.00';
  }
  return `£${Number(value).toFixed(2)}`;
};

const generateTempId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `temp-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
};

const createEmptyEntry = (category: DebtCategoryKey): DraftDebtEntry => {
  const entry: DraftDebtEntry = {
    tempId: generateTempId(),
    category,
    creditorName: '',
    parentCompanyName: null,
    lenderName: null,
    accTypeCode: null,
    accTypeId: null,
    accountTypeName: null,
    accountNumber: '',
    amountOwed: null,
    repaymentAmount: null,
    repaymentFrequency: 'monthly',
    paymentOfferAmount: null,
    paymentOfferFrequency: null,
    paymentOfferId: null,
    paymentOfferMode: null,
    shareBudget: null,
    shareNow: 'N',
    isDigital: false,
    schemaName: null,
    subDomain: null,
    isPriority: category === 'arrears',
    debtType: category === 'arrears' ? 'PRIORITY_DEBTS' : null,
    notes: null,
    arrearsCode: null,
  };

  if (category === 'arrears') {
    entry.source = 'customer_expenses';
  }

  return entry;
};

const sanitizeNumber = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const coerceNumericId = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
    return value;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return null;
    const parsed = Number(trimmed);
    if (Number.isFinite(parsed) && parsed > 0) {
      return parsed;
    }
  }
  return null;
};

// Convert a periodic repayment amount to its monthly equivalent for budgeting/balancing
const toMonthly = (
  amount: number | null | undefined,
  freq: DebtRepaymentFrequencyUi | null | undefined,
): number | null => {
  if (amount === null || amount === undefined) return null;
  if (!Number.isFinite(amount)) return null;
  switch (freq) {
    case 'weekly':
      return ((amount / 7) * 365) / 12;
    case '4weekly':
      return amount * (13 / 12);
    case 'monthly':
      return amount;
    case 'quarterly':
      return amount / 3;
    case 'yearly':
      return amount / 12;
    default:
      return null;
  }
};

const normalizeCreditor = (value: string | null | undefined): string =>
  (value ?? '').trim().toLowerCase();

// Format number for input display: round to 2 decimals and trim trailing zeros
const formatUpToTwoDecimals = (value: number | null | undefined): string => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return '';
  }
  const rounded = Math.round(Number(value) * 100) / 100;
  let str = rounded.toFixed(2);
  str = str.replace(/\.?0+$/, '');
  return str;
};

const hasDuplicate = (
  candidate: DraftDebtEntry,
  collection: DebtFormEntry[],
): boolean => {
  const normalizedCreditor = normalizeCreditor(candidate.creditorName);
  const normalizedAccount = normalizeCreditor(candidate.accountNumber ?? '');

  if (!normalizedCreditor || !normalizedAccount) {
    return false;
  }

  return collection.some((entry) => {
    const entryCreditor = normalizeCreditor(entry.creditorName);
    const entryAccount = normalizeCreditor(entry.accountNumber ?? '');
    const entryId = entry.id ?? entry.tempId;
    const candidateId = candidate.id ?? candidate.tempId;
    if (entryId === candidateId) {
      return false;
    }
    return entryCreditor === normalizedCreditor && entryAccount === normalizedAccount;
  });
};

// ORIGINAL category-aware helpers for filtering account types (kept commented for easy revert)
// const isLoanAccountType = (record: AccountTypeRecord): boolean => {
//   const code = (record.code || '').toUpperCase();
//   const label = (record.label || '').toLowerCase();
//   return (
//     label.includes('loan') ||
//     code === 'UL' ||
//     code === 'ML' ||
//     code === 'EL' ||
//     code === 'FL'
//   );
// };
//
// const PRIORITY_ARREARS_CODES = new Set<string>([
//   'HR', // Housing Rent Arrears
//   'MG', // Mortgage Arrears
//   'SE', // Secured Loan Arrears
//   'CT', // Council Tax Arrears
//   'GA', // Gas - Arrears
//   'EE', // Electricity - Arrears
//   'WS', // Water supply - Arrears
//   'WA', // Water waste - Arrears
// ]);
//
// const isCreditCardCode = (record: AccountTypeRecord): boolean =>
//   (record.code || '').toUpperCase() === 'ZC';
// const isOverdraftCode = (record: AccountTypeRecord): boolean =>
//   (record.code || '').toUpperCase() === 'OD';
//
// const isPriorityDebt = (record: AccountTypeRecord): boolean => {
//   const code = (record.code || '').toUpperCase();
//   const debtType = (record.debtType || '').toUpperCase();
//
//   if (PRIORITY_ARREARS_CODES.has(code)) {
//     return true;
//   }
//
//   return debtType === 'PRIORITY_DEBTS';
// };
//
// const filterAccountTypesForCategory = (
//   category: DebtCategoryKey,
//   options: AccountTypeRecord[],
// ): AccountTypeRecord[] => {
//   switch (category) {
//     case 'loans':
//       return options.filter((r) => isLoanAccountType(r) && !isPriorityDebt(r) && !isCreditCardCode(r) && !isOverdraftCode(r));
//     case 'otherDebts':
//       return options.filter((r) => !isPriorityDebt(r) && !isLoanAccountType(r) && !isCreditCardCode(r) && !isOverdraftCode(r));
//     case 'arrears':
//       return options.filter(isPriorityDebt);
//     default:
//       return options;
//   }
// };

// TEMPORARY: show all account types for all categories (no filtering).
// This is to support debugging / data review for credit cards, loans, others and arrears.
// When removing this temporary behaviour, re-introduce the ORIGINAL filterAccountTypesForCategory above.
const filterAccountTypesForCategory = (
  _category: DebtCategoryKey,
  options: AccountTypeRecord[],
): AccountTypeRecord[] => options;

const DebtCategory: React.FC<DebtCategoryProps> = ({
  label,
  category,
  value,
  onChange,
  placeholder: _placeholder, // Keep for future use
  lenders = [],
  accountTypes = [],
  energyCompanies = [],
  readOnly = false,
  loading = false,
  description: _description, // Keep for future use
  onDuplicateDetected,
  existingCreditorNames = [],
  allExistingDebts,
  availableBudget,
  hidePaymentOffer = false,
  hideAccountType = false,
  density = 'comfortable',
  forceShowAccountType = false,
  cardDebtTypeSelectable = false,
  loanDebtTypeSelectable = false,
  otherDebtTypeDefault = false,
  customerId: customerIdProp,
  addButtonLabel = 'Add debts',
  addAnotherButtonLabel = 'Add another debt',
}) => {
  const { user } = useAuth();
  const { showAlert } = useAlert();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // Track the original entry when editing so we can detect referral-breaking changes
  const editingOriginalRef = useRef<DraftDebtEntry | null>(null);
  // Track when user explicitly clears repayment amount so we can persist 0 instead of null.
  const repaymentClearedRef = useRef<boolean>(false);
  // Track when user explicitly clears payment offer amount so we can delete the offer record.
  const paymentOfferClearedRef = useRef<boolean>(false);

  const [expanded, setExpanded] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState<boolean>(false);
  const [duplicateCompany, setDuplicateCompany] = useState<string>('');
  const [draft, setDraft] = useState<DraftDebtEntry>(() => createEmptyEntry(category));
  const [formErrors, setFormErrors] = useState<DebtFormErrors>({});
  const [companySelection, setCompanySelection] = useState<string>(DEFAULT_COMPANY_OPTION);
  const [manualCompanyName, setManualCompanyName] = useState<string>('');
  const [productSelection, setProductSelection] = useState<string>('');
  const [freepostNotes, setFreepostNotes] = useState<string>('');
  const [isSaving, setIsSaving] = useState<boolean>(false);
  // Synchronous guard against double-submit. React state updates are async/batched,
  // so a fast double-click can fire upsertEntry twice before isSaving re-renders.
  const isSavingRef = useRef<boolean>(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | number | null>(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [entryToDelete, setEntryToDelete] = useState<DebtFormEntry | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [companyDuplicateDialogOpen, setCompanyDuplicateDialogOpen] = useState<boolean>(false);
  const [pendingCompanySelection, setPendingCompanySelection] = useState<string | null>(null);
  // Keep raw text for payment-offer amount so users can type decimals naturally (e.g. ".", "0.", "0.5").
  const [paymentOfferAmountInput, setPaymentOfferAmountInput] = useState<string>('');
  const paymentOfferInputDirtyRef = useRef<boolean>(false);
  // Keep raw text for numeric inputs so users can type decimals naturally (e.g. "12.", ".5").
  // Storing only numbers causes React re-renders to drop trailing decimal points while typing.
  const [amountOwedInput, setAmountOwedInput] = useState<string>('');
  const [repaymentAmountInput, setRepaymentAmountInput] = useState<string>('');
  const amountOwedInputDirtyRef = useRef<boolean>(false);
  const repaymentAmountInputDirtyRef = useRef<boolean>(false);
  // Baseline offer amount as last known from API when the dialog opened (server-backed edits).
  // Stored in state so async refreshes (getPaymentOffer) update the baseline calculation.
  const [originalOfferAmountBaseline, setOriginalOfferAmountBaseline] = useState<number>(0);

  const debugEnabled =
    typeof process !== 'undefined' && process?.env?.NODE_ENV !== 'production';
  const debugLog = React.useCallback(
    (message: string, meta?: Record<string, unknown>) => {
      if (!debugEnabled) return;
      try {
        // eslint-disable-next-line no-console
        console.info('[DebtCategory]', message, meta || {});
      } catch {
        // never throw from logging
      }
    },
    [debugEnabled],
  );

  const companyOptions = useMemo(() => {
    const hiddenSet = new Set(HIDDEN_COMPANY_NAMES.map((n) => (n ?? '').trim().toLowerCase()));

    // Base list from lenders (existing behaviour)
    // Filter out companies where addToDebtList is false (these should not appear in debt lists)
    const baseFromLenders: string[] = Array.isArray(lenders)
      ? Array.from(
        new Set(
          lenders
            .filter((record) => record.addToDebtList === true)
            .map((record) => record.parentCompany)
            .filter((name): name is string => Boolean(name && name.trim().length > 0)),
        ),
      )
      : [];

    const combinedSet = new Set<string>();
    baseFromLenders.forEach((name) => {
      const key = name.trim().toLowerCase();
      if (key && !hiddenSet.has(key)) {
        combinedSet.add(name);
      }
    });

    // For arrears dialog, also include company list from ie-energy-companies-mstr
    if (category === 'arrears' && Array.isArray(energyCompanies)) {
      energyCompanies.forEach((company) => {
        const name = (company?.companyName ?? '').trim();
        const key = name.toLowerCase();
        if (name && !hiddenSet.has(key)) {
          combinedSet.add(name);
        }
      });
    }

    return Array.from(combinedSet).sort((a, b) => a.localeCompare(b));
  }, [lenders, energyCompanies, category]);

  const allAccountTypeOptions = useMemo(() => {
    if (!Array.isArray(accountTypes)) {
      return [] as Array<{ value: string; label: string; record: AccountTypeRecord }>;
    }
    return accountTypes.map((option) => ({
      value: option.code,
      label: option.label,
      record: option,
    }));
  }, [accountTypes]);

  // Normalized set of existing creditor/company names across ALL debt sections.
  // Used to trigger a soft confirmation when user selects a company that already exists.
  const existingCreditorNameSet = useMemo(() => {
    const set = new Set<string>();
    if (Array.isArray(existingCreditorNames)) {
      existingCreditorNames.forEach((name) => {
        const key = normalize(name);
        if (key) {
          set.add(key);
        }
      });
    }
    return set;
  }, [existingCreditorNames]);

  const lenderLookup = useMemo(() => buildArrearsLenderLookup(lenders), [lenders]);

  const selectionContext = useMemo(
    () =>
      computeSelectionContext({
        companySelection,
        productSelection,
        manualCompanyName,
        lenders,
        accountTypes,
        lookup: lenderLookup,
        manual: companySelection === MANUAL_COMPANY_OPTION,
      }),
    [companySelection, productSelection, manualCompanyName, lenders, accountTypes, lenderLookup]
  );

  // Category-aware filtered options for non-arrears categories
  const filteredAccountTypeOptions = useMemo(() => {
    if (category === 'arrears') return [] as Array<{ value: string; label: string; record: AccountTypeRecord }>;
    // Fallback to all accountTypes when no company is selected or context has no options
    const base = (selectionContext.accountTypeOptions && selectionContext.accountTypeOptions.length > 0)
      ? selectionContext.accountTypeOptions
      : accountTypes;
    const narrowed = filterAccountTypesForCategory(category, base);
    return narrowed.map((option) => ({ value: option.code, label: option.label, record: option }));
  }, [selectionContext.accountTypeOptions, accountTypes, category]);

  // Determine whether lender supports payment offer (prefer child-level flag; fallback to parent)
  const lenderSupportsPaymentOffer = useMemo(() => {
    const childFlag = selectionContext.selectedChildRaw?.customerPaymentOffer === 'Y';
    const parentBool = selectionContext.parentRecord?.customerPaymentOffer === true;
    const parentRawFlag = selectionContext.parentRecord?.raw?.customerPaymentOffer === 'Y';
    return Boolean(childFlag || parentBool || parentRawFlag);
  }, [selectionContext.selectedChildRaw, selectionContext.parentRecord]);

  const existingOfferPresent = useMemo(() => {
    const amt = draft.paymentOfferAmount ?? 0;
    return Boolean((typeof amt === 'number' && amt > 0) || draft.paymentOfferId);
  }, [draft.paymentOfferAmount, draft.paymentOfferId]);

  const showPaymentOfferPanel = useMemo(() => {
    // If what's left over is <= 0, only show payment offer if one already exists
    // (so user can reduce/clear it). Otherwise it's hidden.
    if (typeof availableBudget !== 'number' || Number.isNaN(availableBudget)) {
      return false;
    }
    if (availableBudget <= 0) {
      return existingOfferPresent;
    }
    return lenderSupportsPaymentOffer || existingOfferPresent;
  }, [lenderSupportsPaymentOffer, availableBudget, existingOfferPresent]);

  const referenceHelpCompanyName = useMemo(() => {
    if (manualCompanyName.trim()) {
      return manualCompanyName;
    }
    if (productSelection.trim()) {
      return productSelection;
    }
    if (companySelection.trim()) {
      return companySelection;
    }
    if (draft.creditorName?.trim()) {
      return draft.creditorName;
    }
    return undefined;
  }, [companySelection, draft.creditorName, manualCompanyName, productSelection]);

  useReferenceIdHelp({
    companyName: referenceHelpCompanyName,
    backendName: selectionContext.effectiveLenderName ?? draft.lenderName ?? undefined,
  });

  // Debug: whenever the draft payment-offer changes, log it so we can see what the dialog sees.
  React.useEffect(() => {
    if (draft.paymentOfferAmount != null || draft.paymentOfferId != null) {
      // eslint-disable-next-line no-console
      console.log('[Debts] DebtCategory draft payment offer', {
        category,
        creditorName: draft.creditorName,
        accountNumber: draft.accountNumber,
        paymentOfferAmount: draft.paymentOfferAmount,
        paymentOfferMode: draft.paymentOfferMode,
        paymentOfferId: draft.paymentOfferId,
        showPaymentOfferPanel,
      });
    }
  }, [
    category,
    draft.accountNumber,
    draft.creditorName,
    draft.paymentOfferAmount,
    draft.paymentOfferMode,
    draft.paymentOfferId,
    showPaymentOfferPanel,
  ]);

  const repaymentMonthlyForBudget = useMemo(() => {
    return toMonthly(draft.repaymentAmount ?? null, draft.repaymentFrequency ?? null) ?? 0;
  }, [draft.repaymentAmount, draft.repaymentFrequency]);

  const originalRepaymentMonthlyForBudget = useMemo(() => {
    const original = editingOriginalRef.current;
    const serverId = coerceNumericId(original?.id);
    if (!isEditing || !serverId) {
      return 0;
    }
    return toMonthly(original?.repaymentAmount ?? null, original?.repaymentFrequency ?? null) ?? 0;
  }, [isEditing]);

  const offerAmountForBudget = useMemo(() => {
    // Use the raw input string so decimals are reflected live even during typing.
    const raw = (paymentOfferAmountInput ?? '').trim();
    if (!raw || raw === '.') return 0;
    const parsed = parseFloat(raw.replace(/,/g, ''));
    return Number.isFinite(parsed) ? parsed : 0;
  }, [paymentOfferAmountInput]);

  const originalOfferAmountForBudget = useMemo(() => {
    const original = editingOriginalRef.current;
    const serverId = coerceNumericId(original?.id);
    if (!isEditing || !serverId) {
      return 0;
    }
    return originalOfferAmountBaseline;
  }, [isEditing, originalOfferAmountBaseline]);

  // Remaining budget after current payment offer amount (client-side only when API budget is > 0)
  const effectiveAvailableBudget = useMemo(() => {
    if (typeof availableBudget !== 'number' || Number.isNaN(availableBudget)) {
      return null;
    }

    // IMPORTANT:
    // `availableBudget` (balanceAmount) already includes the *saved* repayment + saved payment offer.
    // When editing an existing server-backed debt, compute a baseline that adds those saved values back
    // before subtracting the current (possibly edited) values. This avoids double-subtracting.
    const isEditingExisting =
      isEditing && coerceNumericId(draft.id ?? editingOriginalRef.current?.id) !== null;
    const baseline =
      availableBudget +
      (isEditingExisting ? originalRepaymentMonthlyForBudget : 0) +
      (isEditingExisting ? originalOfferAmountForBudget : 0);

    // Live calc based purely on current field values:
    // baseline - repaymentMonthly - paymentOfferAmount
    return baseline - repaymentMonthlyForBudget - offerAmountForBudget;
  }, [
    availableBudget,
    draft.id,
    isEditing,
    offerAmountForBudget,
    originalOfferAmountForBudget,
    originalRepaymentMonthlyForBudget,
    repaymentMonthlyForBudget,
  ]);

  const paymentOfferExceedsBudget = useMemo(() => {
    // Use live offer amount so error appears while typing (decimals supported).
    if (offerAmountForBudget <= 0) {
      return false;
    }
    if (typeof effectiveAvailableBudget === 'number') {
      return effectiveAvailableBudget < 0;
    }
    return false;
  }, [offerAmountForBudget, effectiveAvailableBudget]);

  const paymentOfferErrorMessage =
    formErrors.paymentOfferAmount ??
    (paymentOfferExceedsBudget ? 'Offer exceeds amount available' : undefined);

  // Determine if current category has a locked account type (e.g., Credit Cards, Overdrafts)
  const lockedCategoryTypeRaw = useMemo(() => LOCKED_CATEGORY_TYPES[category], [category]);
  // When card debt-type selection is enabled for the Cards section, the type is no
  // longer locked — the user picks Credit/Store/Charge card from a dropdown instead.
  const isCardTypeSelectable = cardDebtTypeSelectable && category === 'creditCards';
  // When loan debt-type selection is enabled for the Loans section, the generic
  // account-type dropdown is replaced by a restricted list of loan debt types.
  const isLoanTypeSelectable = loanDebtTypeSelectable && category === 'loans';
  // When enabled for the Others section, the debt-type dropdown is hidden and every
  // entry uses the fixed "Other Non Priority Debt" account type from the master.
  const isOtherTypeDefault = otherDebtTypeDefault && category === 'otherDebts';
  const lockedCategoryType = isCardTypeSelectable ? undefined : lockedCategoryTypeRaw;

  // Resolve the "Other Non Priority Debt" account type from the loaded master.
  // Prefer the known code ('ZO'), then fall back to a label heuristic, so the
  // entry is correctly typed even if the master's wording varies. May be
  // undefined if the master hasn't loaded — the code default below still applies.
  const otherDefaultAccountType = useMemo(() => {
    if (!isOtherTypeDefault || !Array.isArray(accountTypes)) return undefined;
    const byCode = accountTypes.find(
      (rec) => (rec.code ?? '').trim().toUpperCase() === OTHER_NON_PRIORITY_DEBT.code,
    );
    if (byCode) return byCode;
    const matches = (raw: string | null | undefined): boolean => {
      const n = (raw ?? '').trim().toLowerCase();
      if (!n) return false;
      return n === 'other non priority debt'
        || (n.includes('other') && n.includes('non') && n.includes('priority'));
    };
    return accountTypes.find((rec) => matches(rec.label) || matches(rec.code));
  }, [isOtherTypeDefault, accountTypes]);
  const OTHER_DEBT_TYPE_LABEL = otherDefaultAccountType?.label ?? OTHER_NON_PRIORITY_DEBT.label;

  useEffect(() => {
    try {
      const customerDataStr = localStorage.getItem('customerData');
      if (!customerDataStr) return;
      const customerData = JSON.parse(customerDataStr);
      if (customerData?.mobileNumber) {
        setFreepostNotes(
          `I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you. Please call me on ${customerData.mobileNumber} so we can discuss my options.`,
        );
      } else {
        setFreepostNotes(
          "I am in financial difficulty and because of this I have used IE Hub to review my budget.  I have enclosed a copy for your record. You will be able to see from this information that I'm unable to make my full contractual payments to you.",
        );
      }
    } catch {
      // ignore parse errors; keep notes empty
    }
  }, []);

  useEffect(() => {
    if (productSelection !== selectionContext.resolvedProductSelection) {
      setProductSelection(selectionContext.resolvedProductSelection);
    }
  }, [productSelection, selectionContext.resolvedProductSelection]);

  useEffect(() => {
    setDraft((prev) => {
      let next = prev;
      const computedParent = selectionContext.manual
        ? null
        : selectionContext.parentRecord?.parentCompany ??
        (companySelection && companySelection !== MANUAL_COMPANY_OPTION ? companySelection : null);

      if ((prev.parentCompanyName ?? null) !== (computedParent ?? null)) {
        next = next === prev ? { ...prev } : next;
        next.parentCompanyName = computedParent ?? null;
      }

      const computedLender = selectionContext.effectiveLenderName ?? null;
      if ((prev.lenderName ?? null) !== computedLender) {
        next = next === prev ? { ...next } : next;
        next.lenderName = computedLender;
      }

      const computedCreditor = selectionContext.effectiveCreditorName;
      if ((prev.creditorName ?? '') !== (computedCreditor ?? '')) {
        next = next === prev ? { ...next } : next;
        next.creditorName = computedCreditor;
      }

      const computedDigital = Boolean(selectionContext.parentRecord);
      if (prev.isDigital !== computedDigital) {
        next = next === prev ? { ...next } : next;
        next.isDigital = computedDigital;
      }

      const computedSchemaNameRaw =
        (selectionContext.selectedChildRaw as any)?.schemaName ??
        (selectionContext.parentRecord?.raw as any)?.schemaName ??
        null;
      const computedSchemaName =
        typeof computedSchemaNameRaw === 'string' && computedSchemaNameRaw.trim().length > 0
          ? computedSchemaNameRaw
          : null;
      if (prev.schemaName !== computedSchemaName) {
        next = next === prev ? { ...next } : next;
        next.schemaName = computedSchemaName;
      }

      const computedSubDomain =
        (selectionContext.selectedChildRaw as unknown as { subDomain?: string } | undefined)?.subDomain ??
        (selectionContext.parentRecord?.raw as unknown as { subDomain?: string } | undefined)?.subDomain ??
        null;
      if (prev.subDomain !== computedSubDomain) {
        next = next === prev ? { ...next } : next;
        next.subDomain = computedSubDomain;
      }

      return next === prev ? prev : next;
    });
  }, [selectionContext, companySelection]);

  useEffect(() => {
    // Cards section manages its debt type via the dropdown + default-type effect.
    // Skip the generic auto-select/auto-clear here, otherwise it clears the user's
    // selection on every change and fights the default effect (causes flickering).
    if (isCardTypeSelectable) return;
    // Loans section manages its debt type via the restricted dropdown below.
    // Skip the generic auto-select/auto-clear so it doesn't override the user's choice.
    if (isLoanTypeSelectable) return;
    // Others section uses a fixed default debt type (handled by its own effect).
    if (isOtherTypeDefault) return;

    // Auto-apply locked account type when category requires it
    if (lockedCategoryType) {
      setDraft((prev) => {
        if (prev.accTypeCode === lockedCategoryType.code && prev.accountTypeName === lockedCategoryType.label) {
          return prev;
        }
        return {
          ...prev,
          accTypeCode: lockedCategoryType.code,
          accTypeId: prev.accTypeId ?? null,
          accountTypeName: lockedCategoryType.label,
        };
      });
      return; // skip dynamic account type auto-selection for locked categories
    }

    const singleFiltered = filteredAccountTypeOptions.length === 1 ? filteredAccountTypeOptions[0]?.value : null;
    const autoCode = singleFiltered || selectionContext.autoSelectAccountType;
    if (autoCode && draft.accTypeCode !== autoCode) {
      const record =
        selectionContext.accountTypeOptions.find((item) => item.code === autoCode) ??
        accountTypes.find((item) => item.code === autoCode);
      setDraft((prev) => {
        if (prev.accTypeCode === autoCode) {
          return prev;
        }
        return {
          ...prev,
          accTypeCode: autoCode,
          accTypeId: record?.id ?? null,
          accountTypeName: record?.label ?? null,
          debtType: record?.debtType ?? prev.debtType ?? null,
        };
      });
    } else if (
      category !== 'arrears' &&
      !selectionContext.accountTypeRequired &&
      draft.accTypeCode &&
      // Do not auto-clear user selection when account type is allowed before company selection
      !forceShowAccountType
    ) {
      setDraft((prev) => ({
        ...prev,
        accTypeCode: null,
        accTypeId: null,
        accountTypeName: null,
      }));
    }
  }, [
    selectionContext.accountTypeRequired,
    selectionContext.autoSelectAccountType,
    selectionContext.accountTypeOptions,
    accountTypes,
    filteredAccountTypeOptions,
    draft.accTypeCode,
    category,
    forceShowAccountType,
    isCardTypeSelectable,
    isLoanTypeSelectable,
    isOtherTypeDefault,
  ]);

  // Default the Cards section debt type to "Credit card" when none is selected yet.
  useEffect(() => {
    if (!isCardTypeSelectable || draft.accTypeCode) return;
    const def = CARD_DEBT_TYPES[0];
    if (!def) return;
    const record = accountTypes.find((item) => item.code === def.code);
    setDraft((prev) =>
      prev.accTypeCode
        ? prev
        : {
            ...prev,
            accTypeCode: def.code,
            accTypeId: record?.id ?? prev.accTypeId ?? null,
            accountTypeName: record?.label ?? def.label,
            debtType: record?.debtType ?? prev.debtType ?? null,
          },
    );
  }, [isCardTypeSelectable, draft.accTypeCode, accountTypes]);

  // Default the Others section debt type to "Other Non Priority Debt".
  // The Others section never shows a debt-type dropdown; every entry uses this
  // fixed type, resolved from the account-type master by label.
  useEffect(() => {
    if (!isOtherTypeDefault) return;
    // Always default to the fixed "Other Non Priority Debt" code ('ZO') so the
    // payload is never null — even if the account-type master hasn't loaded or
    // doesn't surface this record.
    const code = otherDefaultAccountType?.code ?? OTHER_NON_PRIORITY_DEBT.code;
    setDraft((prev) => {
      if (
        prev.accTypeCode === code &&
        prev.accountTypeName === OTHER_DEBT_TYPE_LABEL &&
        prev.debtType === (otherDefaultAccountType?.debtType ?? 'NON_PRIORITY_DEBTS')
      ) {
        return prev;
      }
      return {
        ...prev,
        accTypeCode: code,
        accTypeId: otherDefaultAccountType?.id ?? prev.accTypeId ?? null,
        accountTypeName: OTHER_DEBT_TYPE_LABEL,
        debtType: otherDefaultAccountType?.debtType ?? 'NON_PRIORITY_DEBTS',
      };
    });
  }, [isOtherTypeDefault, otherDefaultAccountType, OTHER_DEBT_TYPE_LABEL]);

  const resetDialogState = useCallback(() => {
    setDraft(createEmptyEntry(category));
    setFormErrors({});
    setCompanySelection(DEFAULT_COMPANY_OPTION);
    setManualCompanyName('');
    setProductSelection('');
    setIsSaving(false);
    isSavingRef.current = false;
    setIsEditing(false);
    editingOriginalRef.current = null;
    repaymentClearedRef.current = false;
    paymentOfferClearedRef.current = false;
    paymentOfferInputDirtyRef.current = false;
    setPaymentOfferAmountInput('');
    amountOwedInputDirtyRef.current = false;
    repaymentAmountInputDirtyRef.current = false;
    setAmountOwedInput('');
    setRepaymentAmountInput('');
    setOriginalOfferAmountBaseline(0);
  }, [category]);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    resetDialogState();
  };

  const handleAddDebt = () => {
    if (loading) return;
    editingOriginalRef.current = null;
    setIsEditing(false);
    resetDialogState();
    setDialogOpen(true);
  };

  const handleEditDebt = (entry: DebtFormEntry) => {
    if (loading) return;
    setIsEditing(true);
    repaymentClearedRef.current = false;
    paymentOfferClearedRef.current = false;
    paymentOfferInputDirtyRef.current = false;
    // Capture the original entry for comparison on save (detect referral unlink)
    editingOriginalRef.current = {
      ...entry,
    };
    // Debug: log the entry we are about to edit so we can verify payment-offer fields
    // eslint-disable-next-line no-console
    console.log('[Debts] handleEditDebt entry', {
      id: entry.id,
      category: entry.category,
      creditorName: entry.creditorName,
      accountNumber: entry.accountNumber,
      paymentOfferAmount: entry.paymentOfferAmount,
      paymentOfferMode: entry.paymentOfferMode,
      paymentOfferId: entry.paymentOfferId,
    });
    const existingAccountNumberRaw = entry.accountNumber ?? '';
    const userEmail =
      typeof user?.emailAddress === 'string' ? user.emailAddress.trim() : '';
    const isEmailFallback =
      userEmail &&
      existingAccountNumberRaw &&
      existingAccountNumberRaw.trim().toLowerCase() ===
      userEmail.toLowerCase();
    const accountNumberForDraft = isEmailFallback ? '' : existingAccountNumberRaw;
    let baseDraft: DraftDebtEntry = {
      ...entry,
      tempId: entry.tempId ?? generateTempId(),
      accountNumber: accountNumberForDraft,
    };

    // Defensive re-hydration from raw API record for payment offers.
    // In some flows the mapped `paymentOfferAmount`/`paymentOfferMode` on the entry
    // may be lost or stale, but `entry.raw` (CustomerDebtRecord) still has the
    // latest values from `/ie-customer-payment-offer`.
    const raw = (entry as unknown as { raw?: CustomerDebtRecord | undefined })?.raw;
    if (raw) {
      const rawAmount = (raw as any).paymentOfferAmount as number | undefined;
      if (
        (baseDraft.paymentOfferAmount == null || Number.isNaN(baseDraft.paymentOfferAmount)) &&
        typeof rawAmount === 'number' &&
        !Number.isNaN(rawAmount) &&
        rawAmount > 0
      ) {
        baseDraft = {
          ...baseDraft,
          paymentOfferAmount: rawAmount,
        };
      }
      if (!baseDraft.paymentOfferMode && (raw as any).paymentOfferMode) {
        baseDraft = {
          ...baseDraft,
          paymentOfferMode: (raw as any).paymentOfferMode as any,
        };
      }
      if (baseDraft.paymentOfferId == null && (raw as any).paymentOfferId != null) {
        baseDraft = {
          ...baseDraft,
          paymentOfferId: (raw as any).paymentOfferId as number,
        };
      }
    }

    // Baseline offer amount at dialog open (used to avoid double-subtracting against balanceAmount).
    setOriginalOfferAmountBaseline(
      typeof baseDraft.paymentOfferAmount === 'number' && !Number.isNaN(baseDraft.paymentOfferAmount)
        ? baseDraft.paymentOfferAmount
        : 0,
    );

    let derivedCompany = DEFAULT_COMPANY_OPTION;
    let derivedProduct = '';
    let derivedManualName = '';

    const parentName = entry.parentCompanyName ?? null;
    if (parentName) {
      const parentRecord = lenderLookup.byParent.get(normalize(parentName));
      if (parentRecord) {
        derivedCompany = parentRecord.parentCompany;
        const candidateProduct = entry.lenderName ?? entry.creditorName ?? '';
        if (candidateProduct) {
          const childMatch = lenderLookup.childToParent.get(normalize(candidateProduct));
          if (childMatch && normalize(childMatch.parent.parentCompany) === normalize(parentRecord.parentCompany)) {
            derivedProduct = childMatch.childRaw?.lender ?? candidateProduct;
          }
        }
      }
    }

    if (derivedCompany === DEFAULT_COMPANY_OPTION) {
      const candidate = entry.creditorName ?? entry.lenderName ?? '';
      if (candidate) {
        const childMatch = lenderLookup.childToParent.get(normalize(candidate));
        if (childMatch) {
          derivedCompany = childMatch.parent.parentCompany;
          derivedProduct = childMatch.childRaw?.lender ?? candidate;
        } else {
          const parentMatch = lenderLookup.byParent.get(normalize(candidate));
          if (parentMatch) {
            derivedCompany = parentMatch.parentCompany;
          } else {
            derivedCompany = MANUAL_COMPANY_OPTION;
            derivedManualName = candidate;
          }
        }
      } else {
        derivedCompany = MANUAL_COMPANY_OPTION;
      }
    }

    const resolvedManualName =
      derivedCompany === MANUAL_COMPANY_OPTION ? (derivedManualName || entry.creditorName || '') : '';

    setDraft({
      ...baseDraft,
      parentCompanyName: derivedCompany === MANUAL_COMPANY_OPTION ? null : derivedCompany,
      lenderName: derivedProduct ? derivedProduct : null,
      creditorName:
        derivedCompany === MANUAL_COMPANY_OPTION
          ? resolvedManualName
          : derivedProduct || (derivedCompany && derivedCompany !== MANUAL_COMPANY_OPTION ? derivedCompany : entry.creditorName ?? ''),
    });
    setPaymentOfferAmountInput(formatUpToTwoDecimals(baseDraft.paymentOfferAmount));
    amountOwedInputDirtyRef.current = false;
    repaymentAmountInputDirtyRef.current = false;
    setAmountOwedInput(formatUpToTwoDecimals(baseDraft.amountOwed));
    setRepaymentAmountInput(formatUpToTwoDecimals(baseDraft.repaymentAmount));
    setCompanySelection(derivedCompany);
    setManualCompanyName(resolvedManualName);
    setProductSelection(derivedProduct);
    setFormErrors({});
    setDialogOpen(true);

    // Final defensive step: fetch the latest payment-offer from API on edit-open
    // to guarantee the dialog sees the most recent amount/day even if any prior
    // mapping/merging step lost it.
    (async () => {
      try {
        const customerId = (customerIdProp ?? await getCustomerId());
        const debtIdForOffer: number | string | null =
          typeof entry.id === 'number' || typeof entry.id === 'string'
            ? entry.id
            : null;
        const accNum = (entry.accountNumber ?? '').trim();
        if (!customerId || !debtIdForOffer || !accNum) return;

        const resp = await DebtsService.getPaymentOffer(
          customerId as any,
          debtIdForOffer as any,
          accNum,
          { skipErrorCapture: true },
        );
        const payload = (resp?.data as any) ?? null;
        const latest =
          Array.isArray(payload) && payload.length > 0
            ? (payload[payload.length - 1] as any)
            : payload;
        if (latest) {
          setDraft((prev) => ({
            ...prev,
            paymentOfferAmount:
              typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                ? latest.paymentOfferAmount
                : prev.paymentOfferAmount ?? null,
            paymentOfferMode:
              // UI wants the preferred payment day, not the backend constant "M".
              latest.paymentPreferredFrequency ??
              latest.paymentOfferMode ??
              prev.paymentOfferMode ??
              null,
            paymentOfferId:
              latest.id != null
                ? latest.id
                : prev.paymentOfferId ?? null,
          }));
          // Keep the "original" baseline in sync with latest API offer so balanceAmount adjustments are correct.
          if (editingOriginalRef.current) {
            editingOriginalRef.current = {
              ...editingOriginalRef.current,
              paymentOfferAmount:
                typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                  ? latest.paymentOfferAmount
                  : (editingOriginalRef.current.paymentOfferAmount ?? null),
              paymentOfferId:
                latest.id != null
                  ? latest.id
                  : (editingOriginalRef.current.paymentOfferId ?? null),
            };
          }

          // Update baseline offer amount from latest API record (this triggers recalculation).
          setOriginalOfferAmountBaseline(
            typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
              ? latest.paymentOfferAmount
              : 0,
          );
          // If the API fetch updated the offer and the user hasn't started typing, sync the input text too.
          if (!paymentOfferInputDirtyRef.current) {
            const nextText =
              typeof latest.paymentOfferAmount === 'number' && !Number.isNaN(latest.paymentOfferAmount)
                ? formatUpToTwoDecimals(latest.paymentOfferAmount)
                : '';
            setPaymentOfferAmountInput(nextText);
          }
        }
      } catch {
        // ignore - dialog will continue with whatever we already have
      }
    })();
  };

  const handleDeleteDebt = (entry: DebtFormEntry) => {
    if (readOnly || deletingEntryId !== null) return;
    const entryId = entry.id ?? entry.tempId;
    if (!entryId) return; // Safety check - should not happen
    setEntryToDelete(entry);
    setDeleteConfirmOpen(true);
  };

  const confirmDeleteDebt = async () => {
    if (!entryToDelete || readOnly || deletingEntryId !== null) return;
    const entryId = entryToDelete.id ?? entryToDelete.tempId;
    if (!entryId) return; // Safety check - should not happen

    setDeleteConfirmOpen(false);
    setDeletingEntryId(entryId);

    const performDelete = async () => {
      try {
        const customerId = (customerIdProp ?? await getCustomerId());
        const debtId: number | string | null = typeof entryToDelete.id === 'number'
          ? entryToDelete.id
          : typeof entryToDelete.id === 'string' && entryToDelete.id.trim().length > 0
            ? entryToDelete.id.trim()
            : null;
        if (!customerId || !debtId) {
          // No server id - just remove locally
          const nextLocal = value.filter((item) => (item.id ?? item.tempId) !== entryId);
          onChange(nextLocal);
          // Refresh budget/totals (e.g., "what's left over") immediately
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('iehub-debts-changed', {
                  detail: { action: 'deleted', debtId: entryToDelete.id ?? entryToDelete.tempId },
                }),
              );
            }
          } catch {
            // ignore event dispatch failures
          }
          setDeletingEntryId(null);
          setEntryToDelete(null);
          // Show success message
          try {
            window.dispatchEvent(
              new CustomEvent('iehub-alert', {
                detail: {
                  severity: 'success',
                  message: 'Debt deleted successfully',
                  variant: 'filled',
                  duration: 3000,
                },
              })
            );
          } catch {
            // no-op
          }
          return;
        }

        // IMPORTANT (legacy parity):
        // Before deleting a debt, ensure any pending payment offer is reset to 0 so
        // pending totals (pendingPaymentOffer) are cleared server-side.
        try {
          const accNum = (entryToDelete.accountNumber ?? '').trim();
          const directOfferId =
            typeof (entryToDelete as any).paymentOfferId === 'number' &&
            (entryToDelete as any).paymentOfferId > 0
              ? ((entryToDelete as any).paymentOfferId as number)
              : null;

          let offerId: number | string | null = directOfferId;
          if (!offerId && accNum) {
            const offerResp = await DebtsService.getPaymentOffer(
              customerId as any,
              debtId as any,
              accNum,
              { skipErrorCapture: true },
            );
            const payload = (offerResp?.data as any) ?? null;
            const latest =
              Array.isArray(payload) && payload.length > 0
                ? (payload[payload.length - 1] as any)
                : payload;
            offerId = latest?.id != null ? latest.id : null;
          }

          if (offerId) {
            await DebtsService.deletePaymentOffer(offerId, { skipErrorCapture: true });
          }
        } catch {
          // best-effort: do not block delete if offer reset fails
        }

        await DebtsService.deleteDebt(customerId as any, debtId, { skipErrorCapture: true });
        const next = value.filter((item) => (item.id ?? item.tempId) !== entryId);
        onChange(next);
        // Refresh budget/totals (e.g., "what's left over") immediately
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('iehub-debts-changed', {
                detail: { action: 'deleted', debtId },
              }),
            );
          }
        } catch {
          // ignore event dispatch failures
        }
        // Show success message
        try {
          window.dispatchEvent(
            new CustomEvent('iehub-alert', {
              detail: {
                severity: 'success',
                message: 'Debt deleted successfully',
                variant: 'filled',
                duration: 3000,
              },
            })
          );
        } catch {
          // no-op
        }
      } catch {
        // Fallback to local remove even if API errors (user intention is delete)
        const next = value.filter((item) => (item.id ?? item.tempId) !== entryId);
        onChange(next);
        // Refresh budget/totals (e.g., "what's left over") even on API error (best-effort)
        try {
          if (typeof window !== 'undefined') {
            window.dispatchEvent(
              new CustomEvent('iehub-debts-changed', {
                detail: { action: 'deleted', debtId: entryToDelete.id ?? entryToDelete.tempId },
              }),
            );
          }
        } catch {
          // ignore event dispatch failures
        }
        // Show success message even on API error (user intention is delete)
        try {
          window.dispatchEvent(
            new CustomEvent('iehub-alert', {
              detail: {
                severity: 'success',
                message: 'Debt deleted successfully',
                variant: 'filled',
                duration: 3000,
              },
            })
          );
        } catch {
          // no-op
        }
      } finally {
        setDeletingEntryId(null);
        setEntryToDelete(null);
      }
    };
    void performDelete();
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setEntryToDelete(null);
  };

  const handleCompanyDuplicateConfirmAdd = () => {
    if (!pendingCompanySelection) {
      setCompanyDuplicateDialogOpen(false);
      return;
    }
    const selected = pendingCompanySelection;
    setCompanyDuplicateDialogOpen(false);
    setPendingCompanySelection(null);
    // Apply the selection without re-running the duplicate guard.
    handleParentChange(selected, { fromDuplicateConfirm: true });
  };

  const handleCompanyDuplicateCancel = () => {
    // Simply close the dialog and clear the pending value.
    // We never applied the selection yet, so the visible value stays as before.
    setCompanyDuplicateDialogOpen(false);
    setPendingCompanySelection(null);
  };

  const handleParentChange = (parentValue: string, options?: { fromDuplicateConfirm?: boolean }) => {
    const fromDuplicateConfirm = options?.fromDuplicateConfirm ?? false;

    // Soft guard: if this company already exists in ANY debt section and we are adding
    // a new debt (not editing), show a confirmation dialog before applying the selection.
    if (
      !fromDuplicateConfirm &&
      !isEditing &&
      parentValue !== MANUAL_COMPANY_OPTION &&
      parentValue !== DEFAULT_COMPANY_OPTION
    ) {
      const normalizedSelected = normalize(parentValue);
      if (normalizedSelected && existingCreditorNameSet.has(normalizedSelected)) {
        setPendingCompanySelection(parentValue);
        setCompanyDuplicateDialogOpen(true);
        return;
      }
    }

    setCompanySelection(parentValue);
    setProductSelection('');
    setFormErrors((prev) => {
      const {
        company: _company,
        manualCompany: _manual,
        product: _product,
        creditorName: _creditor,
        accTypeCode: _accType,
        accountNumber: _accNum,
        ...rest
      } = prev;
      return rest;
    });

    if (parentValue === MANUAL_COMPANY_OPTION) {
      setManualCompanyName('');
      setDraft((prev) => ({
        ...prev,
        parentCompanyName: null,
        lenderName: null,
        creditorName: '',
        isDigital: false,
        schemaName: null,
        subDomain: null,
      }));
      return;
    }

    if (parentValue === DEFAULT_COMPANY_OPTION) {
      setManualCompanyName('');
      setDraft((prev) => ({
        ...prev,
        parentCompanyName: null,
        lenderName: null,
        creditorName: '',
        isDigital: false,
        schemaName: null,
        subDomain: null,
      }));
      return;
    }

    const parentRecord = lenderLookup.byParent.get(normalize(parentValue));
    const parentSchemaNameRaw = (parentRecord?.raw as any)?.schemaName;
    setManualCompanyName('');
    setDraft((prev) => ({
      ...prev,
      parentCompanyName: parentRecord ? parentRecord.parentCompany : parentValue,
      lenderName: null,
      creditorName: parentRecord ? parentRecord.parentCompany : parentValue,
      isDigital: Boolean(parentRecord),
      schemaName:
        typeof parentSchemaNameRaw === 'string' && parentSchemaNameRaw.trim().length > 0
          ? parentSchemaNameRaw
          : null,
      subDomain: (parentRecord?.raw as unknown as { subDomain?: string } | undefined)?.subDomain ?? null,
    }));
  };

  // Manual company name is now handled via FreeSelect (see dialog content)

  const handleProductChange = (nextValue: string) => {
    const parentRecord =
      companySelection !== MANUAL_COMPANY_OPTION && companySelection !== DEFAULT_COMPANY_OPTION
        ? lenderLookup.byParent.get(normalize(companySelection))
        : undefined;
    setProductSelection(nextValue);
    setDraft((prev) => ({
      ...prev,
      lenderName: nextValue ? nextValue : null,
      creditorName:
        nextValue ||
        (companySelection === MANUAL_COMPANY_OPTION
          ? manualCompanyName
          : parentRecord?.parentCompany ?? prev.creditorName),
    }));
    // Clear product error when user selects a product
    setFormErrors((prev) => {
      const { product: _product, ...rest } = prev;
      return rest;
    });
  };

  const handleFieldChange = <K extends keyof DraftDebtEntry>(field: K, value: DraftDebtEntry[K]) => {
    setDraft((prev) => ({
      ...prev,
      [field]: value,
    }));
    // Clear field-specific errors when user updates the field
    if (field === 'repaymentFrequency') {
      setFormErrors((prev) => {
        const { repaymentFrequency: _freq, ...rest } = prev;
        return rest;
      });
    } else if (field === 'paymentOfferAmount') {
      setFormErrors((prev) => {
        const { paymentOfferAmount: _offer, ...rest } = prev;
        return rest;
      });
    } else if (field === 'notes') {
      setFormErrors((prev) => {
        const { notes: _notes, ...rest } = prev;
        return rest;
      });
    }
  };

  const validateEntry = (entry: DraftDebtEntry): DebtFormErrors => {
    const errors: DebtFormErrors = {};

    if (!companySelection || companySelection === DEFAULT_COMPANY_OPTION) {
      errors.company = 'Please select a company';
    }

    /**
     * Company validation
     *
     * There are three modes:
     * - Explicit manual mode: user selected "I can't find my company" (MANUAL_COMPANY_OPTION)
     * - Lookup-backed mode: lender comes from ie-mstr (selectionContext.parentRecord present)
     * - Fallback/manual-like mode: company comes from an external list (e.g. energy mstr) and
     *   is *not* in the lender lookup. In this case `selectionContext.manual` may be true, but
     *   we must NOT require `manualCompanyName` because that field is not shown in the UI.
     */
    const isExplicitManualCompany = companySelection === MANUAL_COMPANY_OPTION;

    if (selectionContext.manual && isExplicitManualCompany) {
      if (!manualCompanyName.trim()) {
        errors.manualCompany = 'Please enter the company name';
      }
      // When using manual company entry, rely on the "Other Company name" field
      // and avoid showing a duplicate error on the main company select.
    } else if (!selectionContext.parentRecord && !selectionContext.manual) {
      // Only flag "invalid company" when we genuinely expected a lender lookup record.
      // Fallback/manual-like flows (e.g. energy companies) are allowed without a parentRecord.
      errors.company = 'Please select a valid company';
    }

    if (selectionContext.productRequired && !selectionContext.resolvedProductSelection) {
      errors.product = 'Please select a product';
    }

    if (
      // Only enforce a separate creditorName error when not in manual mode.
      // In manual mode, the "Other Company name" field carries the required validation.
      !selectionContext.manual &&
      (!selectionContext.effectiveCreditorName || !selectionContext.effectiveCreditorName.trim())
    ) {
      errors.creditorName = 'Company name is required';
    }

    // Require account type wherever the debt type dropdown is shown
    const shouldRequireAccountType =
      !hideAccountType &&
      !lockedCategoryType &&
      !isCardTypeSelectable &&
      !isLoanTypeSelectable &&
      !isOtherTypeDefault &&
      category !== 'arrears' &&
      (forceShowAccountType || selectionContext.showAccountType);

    if (shouldRequireAccountType && !entry.accTypeCode) {
      errors.accTypeCode = 'Select an account type';
    }

    // Cards section: a card debt type (Credit/Store/Charge card) must be selected.
    if (isCardTypeSelectable && !entry.accTypeCode) {
      errors.accTypeCode = 'Select a card type';
    }

    // Loans section: a loan debt type must be selected.
    if (isLoanTypeSelectable && !entry.accTypeCode) {
      errors.accTypeCode = 'Select a loan type';
    }

    if (!hideAccountType && category === 'arrears' && !entry.accTypeCode) {
      errors.accTypeCode = 'Select the arrears type';
    }

    if (!entry.repaymentFrequency) {
      errors.repaymentFrequency = 'Select payment frequency';
    }

    // Others section: notes are mandatory.
    if (isOtherTypeDefault && !(entry.notes ?? '').trim()) {
      errors.notes = 'Please add a note';
    }

    // Payment offer amount can't exceed available budget (only when API budget was > 0)
    const paymentOfferAmount =
      typeof entry.paymentOfferAmount === 'number' && !Number.isNaN(entry.paymentOfferAmount)
        ? entry.paymentOfferAmount
        : null;
    if (
      typeof availableBudget === 'number' &&
      !Number.isNaN(availableBudget) &&
      paymentOfferAmount !== null &&
      paymentOfferAmount > 0
    ) {
      // Align with the UI "Amount available in your budget" calculation:
      // balanceAmount already includes saved repayment + saved offer when editing an existing debt.
      const repaymentMonthly =
        toMonthly(entry.repaymentAmount ?? null, entry.repaymentFrequency ?? null) ?? 0;
      const original = editingOriginalRef.current;
      const isEditingExisting =
        isEditing && coerceNumericId(entry.id ?? original?.id) !== null;
      const originalRepaymentMonthly =
        isEditingExisting
          ? (toMonthly(original?.repaymentAmount ?? null, original?.repaymentFrequency ?? null) ?? 0)
          : 0;
      const originalOffer =
        isEditingExisting
          ? (typeof original?.paymentOfferAmount === 'number' && !Number.isNaN(original.paymentOfferAmount)
            ? original.paymentOfferAmount
            : 0)
          : 0;
      const baseline =
        availableBudget +
        (isEditingExisting ? originalRepaymentMonthly : 0) +
        (isEditingExisting ? originalOffer : 0);
      const remaining = baseline - repaymentMonthly - paymentOfferAmount;

      if (typeof remaining === 'number' && remaining < 0) {
        errors.paymentOfferAmount = 'Offer exceeds amount available';
      }
    }

    return errors;
  };

  const upsertEntry = async () => {
    if (readOnly || isSavingRef.current || isSaving) return;
    isSavingRef.current = true;
    const errors = validateEntry(draft);
    setFormErrors(errors);
    if (Object.keys(errors).length > 0) {
      isSavingRef.current = false;
      return;
    }

    // Auto-set accountNumber to user email (field is hidden from UI)
    const emailFallback =
      typeof user?.emailAddress === 'string' ? user.emailAddress.trim() : '';
    const accountNumberValue = emailFallback || '';
    const isLockedCategory = Boolean(lockedCategoryType);
    const effectiveAccTypeCode = isLockedCategory
      ? lockedCategoryType!.code
      : isOtherTypeDefault
        // Others section has no dropdown — always persist the fixed "Other Non
        // Priority Debt" code ('ZO'), even if the draft was reset/edited to null.
        ? (draft.accTypeCode ?? otherDefaultAccountType?.code ?? OTHER_NON_PRIORITY_DEBT.code)
        : (draft.accTypeCode ?? null);
    const effectiveAccTypeRecord =
      effectiveAccTypeCode
        ? accountTypes.find((item) => item.code === effectiveAccTypeCode)
        : undefined;
    const effectiveCreditorName =
      selectionContext.effectiveCreditorName?.trim() || draft.creditorName || '';

    setIsSaving(true);

    const repaymentAmountSanitized = sanitizeNumber(draft.repaymentAmount);
    const repaymentAmountForApi =
      repaymentClearedRef.current && repaymentAmountSanitized === null
        ? 0
        : repaymentAmountSanitized;

    // Persist payment-offer amount from the input string so reopening the dialog
    // immediately after save has the value even before a refetch/enrichment.
    const paymentOfferAmountForSave = (() => {
      const existingDraftOffer =
        typeof draft.paymentOfferAmount === 'number' && !Number.isNaN(draft.paymentOfferAmount)
          ? draft.paymentOfferAmount
          : null;
      if (paymentOfferClearedRef.current) {
        return 0;
      }
      if (offerAmountForBudget > 0) {
        return offerAmountForBudget;
      }
      return existingDraftOffer;
    })();

    const sanitized: DebtFormEntry = {
      ...draft,
      parentCompanyName: selectionContext.manual
        ? null
        : selectionContext.parentRecord?.parentCompany ?? draft.parentCompanyName ?? null,
      lenderName: selectionContext.effectiveLenderName,
      creditorName: selectionContext.effectiveCreditorName.trim(),
      accountNumber: accountNumberValue,
      accTypeCode: effectiveAccTypeCode,
      accTypeId: effectiveAccTypeRecord?.id ?? (effectiveAccTypeCode ? draft.accTypeId ?? null : null),
      accountTypeName: isLockedCategory
        ? (lockedCategoryType!.label)
        : (effectiveAccTypeRecord?.label ?? (effectiveAccTypeCode ? draft.accountTypeName ?? null : null)),
      debtType:
        effectiveAccTypeRecord?.debtType ??
        draft.debtType ??
        (category === 'arrears' ? 'PRIORITY_DEBTS' : draft.debtType ?? null),
      amountOwed: sanitizeNumber(draft.amountOwed),
      repaymentAmount: repaymentAmountForApi,
      paymentOfferAmount: paymentOfferAmountForSave,
      shareNow: draft.shareNow ?? 'N',
      arrearsCode: draft.arrearsCode ?? (category === 'arrears' ? effectiveAccTypeCode ?? null : null),
      source:
        draft.source ??
        editingOriginalRef.current?.source ??
        (category === 'arrears' ? 'customer_expenses' : 'customer_debt'),
    };

    try {
      // Persist to API immediately (create or update)
      const customerId = (customerIdProp ?? await getCustomerId());
      if (!customerId) {
        // Can't persist; fall back to local update
        const identifier = sanitized.id ?? sanitized.tempId;
        const nextLocal = value.some((entry) => (entry.id ?? entry.tempId) === identifier)
          ? value.map((entry) => ((entry.id ?? entry.tempId) === identifier ? sanitized : entry))
          : [...value, sanitized];
        onChange(nextLocal);
        handleCloseDialog();
        return;
      }

      const normalizeForCompare = (value: string | null | undefined): string =>
        (value ?? '').trim().toLowerCase();
      const original = editingOriginalRef.current;
      const originalSourceRaw = original?.source as string | undefined;
      const baseSource = sanitized.source ?? originalSourceRaw ?? null;
      const originalSource = normalizeForCompare(originalSourceRaw);
      const isReferralDebt =
        originalSource === 'customer_profile' || originalSource === 'referred';
      const companyChanged =
        isReferralDebt &&
        normalizeForCompare(original?.creditorName) !== normalizeForCompare(sanitized.creditorName);
      const accTypeChanged =
        isReferralDebt &&
        normalizeForCompare(original?.accTypeCode ?? original?.accountTypeName) !==
        normalizeForCompare(sanitized.accTypeCode ?? sanitized.accountTypeName);

      const inferredSource =
        isReferralDebt && (companyChanged || accTypeChanged)
          ? 'unlink_customer_profile'
          : baseSource ??
          (category === 'arrears'
            ? 'customer_expenses'
            : category === 'creditCards'
              ? 'creditcard'
              : category === 'loans'
                ? 'loan'
                : category === 'overdrafts'
                  ? 'overdraft'
                  : category === 'otherDebts'
                    ? 'otherdebts'
                    : 'customer_debt');

      // Robustly coerce an existing debt id (including from the original/referral record)
      // so that edits to referred debts always use updateDebt (PUT) rather than createDebt (POST).
      // Accepts both legacy numeric IDs and MongoDB ObjectId strings.
      const coerceDebtId = (value: unknown): number | string | null => {
        if (typeof value === 'number' && Number.isFinite(value) && value > 0) {
          return value;
        }
        if (typeof value === 'string') {
          const trimmed = value.trim();
          if (!trimmed) return null;
          const parsed = Number(trimmed);
          if (Number.isFinite(parsed) && parsed > 0) return parsed;
          // Accept MongoDB ObjectId strings (24 hex chars)
          if (/^[a-f\d]{24}$/i.test(trimmed)) return trimmed;
        }
        return null;
      };

      const existingId = coerceDebtId(
        (sanitized as { id?: number | string | null })?.id ??
        (original as ({ id?: number | string | null } | null))?.id ??
        (original as ({ raw?: { id?: number | string | null } } | null))?.raw?.id,
      );

      const payload: DebtUpsertRequest = {
        ...(existingId != null ? { id: existingId } : {}),
        customerId: customerId as any,
        creditor: sanitized.creditorName,
        accTypeCode: sanitized.accTypeCode ?? null,
        accountTypeName: sanitized.accountTypeName ?? null,
        accountNumber: sanitized.accountNumber ?? null,
        amountOwed: sanitized.amountOwed ?? null,
        repayment: sanitized.repaymentAmount ?? null,
        frequency: mapDebtFrequencyUiToApi(sanitized.repaymentFrequency) ?? null,
        notes: sanitized.notes ?? null,
        shareBudget: sanitized.shareBudget ?? null,
        source: inferredSource,
        debtType: sanitized.debtType ?? (category === 'arrears' ? 'PRIORITY_DEBTS' : 'NON_PRIORITY_DEBTS'),
        repaymentMonthly: toMonthly(sanitized.repaymentAmount ?? null, sanitized.repaymentFrequency ?? null),
      };

      const isUpdate = existingId != null;
      const resp = isUpdate
        ? await DebtsService.updateDebt(payload, { skipErrorCapture: true })
        : await DebtsService.createDebt(payload, { skipErrorCapture: true });
      const saved = resp?.data as CustomerDebtRecord | undefined;
      debugLog('debt saved', {
        isUpdate,
        debtId: saved?.id ?? null,
        creditor: saved?.creditor ?? payload.creditor,
        accountNumber: saved?.accountNumber ?? payload.accountNumber,
        accTypeCode: saved?.accTypeCode ?? payload.accTypeCode,
      });

      // Debt saved successfully — update local state and close dialog immediately.
      // Secondary operations (payment offer, sharing) run in the background.
      const identifier = sanitized.id ?? sanitized.tempId;
      const serverId = saved?.id ?? sanitized.id;
      const withId: DebtFormEntry = {
        ...sanitized,
        source: inferredSource,
        ...(serverId !== undefined ? { id: serverId } : {}),
      };
      const next = value.some((entry) => (entry.id ?? entry.tempId) === identifier)
        ? value.map((entry) => ((entry.id ?? entry.tempId) === identifier ? withId : entry))
        : [...value, withId];
      onChange(next);
      handleCloseDialog();

      // ── Background: payment offer + sharing (non-blocking) ──
      const bgCustomerId = customerId;
      const bgSanitized = { ...sanitized };
      const bgPaymentOfferCleared = paymentOfferClearedRef.current;
      (async () => {
        try {
          // Payment offer upsert if present
          const offerAmount = bgSanitized.paymentOfferAmount ?? 0;
          if (saved && saved.id != null && offerAmount && offerAmount > 0) {
            const poPayload: PaymentOfferUpsertRequest = {
              customerId: bgCustomerId as any,
              debtId: saved.id as any,
              lenderName: saved.lenderName || saved.creditor,
              accountNumber: saved.accountNumber ?? null,
              schemaName: (bgSanitized.schemaName ?? saved.schemaName) ?? null,
              paymentOfferAmount: offerAmount,
              paymentOfferMode: 'M' as any,
              paymentPreferredFrequency: (bgSanitized.paymentOfferMode ?? null) as any,
              userId: typeof user?.userId === 'number' ? user.userId : null,
              lastUpdatedBy: typeof user?.userId === 'number' ? user.userId : null,
              ...(bgSanitized.paymentOfferId != null ? { id: bgSanitized.paymentOfferId } : {}),
            };
            try {
              if (poPayload.id != null) {
                await DebtsService.updatePaymentOffer(poPayload.id, poPayload, { skipErrorCapture: true });
              } else {
                await DebtsService.createPaymentOffer(poPayload, { skipErrorCapture: true });
              }
            } catch {
              console.warn('[Debts] Payment offer upsert failed', { poPayload });
            }
          } else if (saved && saved.id != null && bgPaymentOfferCleared) {
            const existingOfferId =
              (typeof bgSanitized.paymentOfferId === 'number' && bgSanitized.paymentOfferId > 0
                ? bgSanitized.paymentOfferId
                : typeof editingOriginalRef.current?.paymentOfferId === 'number'
                  ? editingOriginalRef.current?.paymentOfferId
                  : null);
            if (typeof existingOfferId === 'number' && existingOfferId > 0) {
              try {
                const poPayload: PaymentOfferUpsertRequest = {
                  customerId: bgCustomerId as any,
                  debtId: saved.id as any,
                  lenderName: saved.lenderName || saved.creditor,
                  accountNumber: saved.accountNumber ?? null,
                  schemaName: (bgSanitized.schemaName ?? saved.schemaName) ?? null,
                  paymentOfferAmount: 0,
                  paymentOfferMode: 'M' as any,
                  paymentPreferredFrequency: null,
                  userId: typeof user?.userId === 'number' ? user.userId : null,
                  lastUpdatedBy: typeof user?.userId === 'number' ? user.userId : null,
                  id: existingOfferId,
                };
                await DebtsService.updatePaymentOffer(existingOfferId, poPayload, { skipErrorCapture: true });
              } catch {
                console.warn('[Debts] Payment offer clear-to-0 failed', { existingOfferId });
              }
            }
          }

          // Notify listeners
          try {
            if (typeof window !== 'undefined') {
              window.dispatchEvent(
                new CustomEvent('iehub-debts-changed', { detail: { debtId: saved?.id } }),
              );
            }
          } catch { /* ignore */ }

          // Upsert sharing
          try {
            if (saved && saved.id != null) {
              const lookups = await SharingOrchestrator.fetchLookups();
              const existing = await SharingOrchestrator.fetchExisting(bgCustomerId as any);
              await SharingOrchestrator.upsertForDebt(bgCustomerId as any, saved, lookups, existing, {
                freepostNotes,
              });
              debugLog('upsertForDebt completed', { debtId: saved.id });
            }
          } catch (err) {
            const anyErr = err as any;
            debugLog('upsertForDebt errored', {
              debtId: saved?.id ?? null,
              error: anyErr?.message ?? anyErr?.toString?.(),
            });
          }
        } catch {
          // background tasks best-effort
        }
      })();
    } catch {
      // On API error, still update locally to avoid data loss
      const identifier = sanitized.id ?? sanitized.tempId;
      const next = value.some((entry) => (entry.id ?? entry.tempId) === identifier)
        ? value.map((entry) => ((entry.id ?? entry.tempId) === identifier ? sanitized : entry))
        : [...value, sanitized];
      onChange(next);
      handleCloseDialog();
    } finally {
      setIsSaving(false);
      isSavingRef.current = false;
      repaymentClearedRef.current = false;
      paymentOfferClearedRef.current = false;
    }
  };

  const handleAccountTypeChange = (code: string) => {
    const trimmedCode = code?.trim();
    const record =
      accountTypes.find((item) => item.code === trimmedCode) ??
      selectionContext.accountTypeOptions.find((item) => item.code === trimmedCode);
    setDraft((prev) => ({
      ...prev,
      accTypeCode: trimmedCode && trimmedCode.length > 0 ? trimmedCode : null,
      accTypeId: record?.id ?? null,
      accountTypeName: record?.label ?? null,
      debtType: record?.debtType ?? prev.debtType ?? null,
    }));
    // Clear account type error when user selects an account type
    setFormErrors((prev) => {
      const { accTypeCode: _accType, ...rest } = prev;
      return rest;
    });
  };

  // Handle card debt-type selection (Credit/Store/Charge card) for the Cards section.
  // Falls back to the CARD_DEBT_TYPES label when the backend account-type record
  // is not yet available (e.g. master not reseeded), so the chosen type still persists.
  const handleCardDebtTypeChange = (code: string) => {
    const trimmedCode = code?.trim();
    const record = accountTypes.find((item) => item.code === trimmedCode);
    const fallback = CARD_DEBT_TYPES.find((opt) => opt.code === trimmedCode);
    setDraft((prev) => ({
      ...prev,
      accTypeCode: trimmedCode && trimmedCode.length > 0 ? trimmedCode : null,
      accTypeId: record?.id ?? null,
      accountTypeName: record?.label ?? fallback?.label ?? null,
      debtType: record?.debtType ?? prev.debtType ?? null,
    }));
    setFormErrors((prev) => {
      const { accTypeCode: _accType, ...rest } = prev;
      return rest;
    });
  };

  // Handle loan debt-type selection (BNPL / Personal / Employer loan / Secured
  // Loan / Mortgage / Motor Finance / Private Student Loan) for the Loans section.
  // Falls back to the LOAN_DEBT_TYPES label when the backend account-type record
  // is not yet available (e.g. master not reseeded), so the chosen type persists.
  const handleLoanDebtTypeChange = (code: string) => {
    const trimmedCode = code?.trim();
    const record = accountTypes.find((item) => item.code === trimmedCode);
    const fallback = LOAN_DEBT_TYPES.find((opt) => opt.code === trimmedCode);
    setDraft((prev) => ({
      ...prev,
      accTypeCode: trimmedCode && trimmedCode.length > 0 ? trimmedCode : null,
      accTypeId: record?.id ?? null,
      accountTypeName: record?.label ?? fallback?.label ?? null,
      debtType: record?.debtType ?? prev.debtType ?? null,
    }));
    setFormErrors((prev) => {
      const { accTypeCode: _accType, ...rest } = prev;
      return rest;
    });
  };

  const currentEntries = value ?? [];
  const hasEntries = currentEntries.length > 0;

  if (loading) {
    return (
      <Box>
        <Paper
          elevation={0}
          sx={{
            backgroundColor: 'white',
            borderRadius: 3,
            border: '1px solid #e6e6e6',
            overflow: 'hidden',
            p: 2,
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
            }}
          >
            <Typography variant="h6" sx={{ fontSize: '20px', fontWeight: 400 }}>
              {label}
            </Typography>
            <Skeleton variant="rectangular" width={100} height={28} sx={{ borderRadius: 2 }} />
          </Box>
          <Stack spacing={1.5}>
            {[1, 2].map((i) => (
              <Skeleton key={i} variant="rectangular" height={72} sx={{ borderRadius: 2 }} />
            ))}
          </Stack>
        </Paper>
      </Box>
    );
  }

  return (
    <Box>
      <Paper
        elevation={0}
        sx={{
          backgroundColor: 'white',
          borderRadius: 3,
          border: '1px solid #e6e6e6',
          overflow: 'hidden',
          p: 2,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            cursor: hasEntries ? 'pointer' : 'default',
          }}
          onClick={() => hasEntries && setExpanded((prev) => !prev)}
        >
          <Stack direction="row" spacing={1.75} alignItems="center">
            <Typography
              variant="h6"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '22px', md: '20px' },
                lineHeight: '24px',
              }}
            >
              {label}
            </Typography>
            {hasEntries && (
              <Chip
                label={currentEntries.length}
                size="small"
                sx={{
                  fontWeight: 400,
                  fontSize: '18px',
                  backgroundColor: '#d4e50e',
                  color: '#003d40',
                  height: '24px',
                  borderRadius: '99px',
                  '& .MuiChip-label': {
                    px: 0.5,
                  }
                }}
              />
            )}
          </Stack>

          {/* Add button or collapse icon */}
          <Stack direction="row" spacing={1} alignItems="center">
            {!hasEntries && !readOnly && (
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon />}
                onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
                  event.stopPropagation();
                  handleAddDebt();
                }}
                disabled={loading}
                sx={{
                  backgroundColor: '#1a5053',
                  borderRadius: '3px',
                  px: 1,
                  py: 0.5,
                  fontSize: '14px',
                  fontWeight: 500,
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: '#155f54',
                  },
                  '& .MuiButton-startIcon': {
                    marginRight: '4px',
                  }
                }}
              >
                {addButtonLabel}
              </Button>
            )}
            {hasEntries && (
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  setExpanded((prev) => !prev);
                }}
                sx={{
                  p: 0,
                }}
              >
                {expanded ? <ExpandLessIcon sx={{ fontSize: 24 }} /> : <ExpandMoreIcon sx={{ fontSize: 24 }} />}
              </IconButton>
            )}
          </Stack>
        </Box>

        {/* Expandable content */}
        <Collapse in={expanded && hasEntries} unmountOnExit>
          <Box mt={2.75}>
            <Stack spacing={1.25}>
              {currentEntries.map((entry) => {
                const entryId = entry.id ?? entry.tempId;
                return (
                  <Paper
                    key={entryId}
                    elevation={0}
                    sx={{
                      borderRadius: 2,
                      backgroundColor: 'white',
                      border: '0px solid #e6e6e6',
                      p: 2,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                    }}
                  >
                    <Stack spacing={0.25}>
                      <Typography
                        variant="subtitle1"
                        sx={{
                          fontWeight: 700,
                          fontSize: '16px',
                          lineHeight: '24px',
                          color: '#003d40',
                        }}
                      >
                        {entry.creditorName || 'Unknown company'}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: '14px',
                          lineHeight: '20px',
                          color: '#6b7a7b',
                        }}
                      >
                        Amount owed: {formatCurrency(entry.amountOwed)}
                      </Typography>
                    </Stack>

                    <Stack direction="row" spacing={2} alignItems="center">
                      {!readOnly && (
                        <>
                          <IconButton
                            size="small"
                            onClick={() => handleEditDebt(entry)}
                            disabled={deletingEntryId !== null}
                            sx={{ p: 0 }}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            onClick={() => handleDeleteDebt(entry)}
                            disabled={deletingEntryId !== null}
                            sx={{
                              p: 0,
                              '&:disabled': {
                                opacity: 0.6,
                              },
                            }}
                          >
                            {deletingEntryId === (entry.id ?? entry.tempId) ? (
                              <CircularProgress size={16} sx={{ color: 'inherit' }} />
                            ) : (
                              <DeleteIcon fontSize="small" />
                            )}
                          </IconButton>
                        </>
                      )}
                    </Stack>
                  </Paper>
                );
              })}
            </Stack>

            {/* Add another debt button */}
            {!readOnly && (
              <Button
                fullWidth
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={handleAddDebt}
                disabled={loading}
                sx={{
                  mt: 1.25,
                  border: '2px dashed #e1e5e5',
                  borderRadius: 3,
                  py: 1.875,
                  fontSize: '18px',
                  fontWeight: 700,
                  color: '#003d40',
                  textTransform: 'none',
                  backgroundColor: 'transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 61, 64, 0.04)',
                    border: '2px dashed #e1e5e5',
                  },
                }}
              >
                {addAnotherButtonLabel}
              </Button>
            )}
          </Box>
        </Collapse>
      </Paper>

      <Dialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{
          sx: {
            borderRadius: isMobile ? 0 : 3,
            maxHeight: '90vh',
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '22px', fontWeight: 400, lineHeight: '24px' }}>
            {label}
          </Typography>
          <IconButton
            onClick={handleCloseDialog}
            size="small"
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '50%',
              p: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          dividers
          sx={{
            px: 2,
            py: density === 'compact' ? 1.25 : 2,
            maxHeight: 'calc(90vh - 180px)',
            overflowY: 'auto',
          }}
        >
          <Stack spacing={density === 'compact' ? 1.0 : 2}>
            {/* Company Name (required) */}
            <Select
              label="Company name"
              dialogLabel="Select a Creditor"
              options={[
                { value: MANUAL_COMPANY_OPTION, label: 'I can\'t find my company' },
                ...companyOptions.map(option => ({ value: option, label: option })),
              ]}
              value={companySelection}
              onChange={(value) => handleParentChange(String(value))}
              error={Boolean(formErrors.company || formErrors.creditorName)}
              helperText={formErrors.company ?? formErrors.creditorName}
              validation={{ required: true }}
              deferValidation={true}
              disabled={readOnly}
              placeholder="Select a Company"
              helperTop="Select your creditor or choose 'I can't find my company' if not listed"
              showErrorBelow={false}
              searchable={true}
              fullWidth
              dataPrivate={false}
            />

            {companySelection === MANUAL_COMPANY_OPTION && (
              <FreeSelect
                label="Other Company name"
                options={[]}
                value={manualCompanyName}
                dataPrivate={false}
                onChange={(nextValue) => {
                  const stringValue =
                    typeof nextValue === 'string'
                      ? nextValue
                      : nextValue != null
                        ? String(nextValue)
                        : '';

                  // Always reflect what the user typed (prevents "last character removed" snapback).
                  setManualCompanyName(stringValue);

                  // If what the user typed exactly matches a real company option (case-insensitive),
                  // automatically switch the main company select to that company and behave as if
                  // the user selected it from the dropdown (incl. duplicate confirmation behavior).
                  const normalizedTyped = normalize(stringValue);
                  const matchedCompany =
                    normalizedTyped && Array.isArray(companyOptions)
                      ? companyOptions.find((name) => normalize(name) === normalizedTyped) ?? null
                      : null;

                  // Maintain manual-mode draft while the match is being resolved (or if it doesn't match).
                  setDraft((prev) => ({
                    ...prev,
                    parentCompanyName: null,
                    creditorName: stringValue,
                  }));
                  setFormErrors((prev) => {
                    const { manualCompany: _manual, creditorName: _creditor, ...rest } = prev;
                    return rest;
                  });

                  if (matchedCompany) {
                    handleParentChange(matchedCompany);
                  }
                }}
                error={Boolean(formErrors.manualCompany)}
                helperText={formErrors.manualCompany}
                disabled={readOnly}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
                helperTop="Start typing to search our Freepost list, or enter the company name manually"
                loadFreepostLenders={true}
              />
            )}

            {selectionContext.showProduct && (
              <Select
                label="Product name"
                dialogLabel={`Select a Product`}
                options={selectionContext.productOptions}
                value={productSelection}
                onChange={(value) => handleProductChange(String(value))}
                error={Boolean(formErrors.product)}
                helperText={formErrors.product}
                disabled={readOnly}
                placeholder="Select a product"
                showErrorBelow={true}
                fullWidth
                dataPrivate={false}
              />
            )}

            {!hideAccountType && category !== 'arrears' && !isCardTypeSelectable && !isLoanTypeSelectable && !isOtherTypeDefault && (forceShowAccountType || selectionContext.showAccountType) && !lockedCategoryType && (
              <Select
                label="Debt type"
                dialogLabel="Select an Account Type"
                options={filteredAccountTypeOptions.map(option => ({ value: option.value, label: option.label }))}
                value={draft.accTypeCode ?? ''}
                onChange={(value) => handleAccountTypeChange(String(value))}
                error={Boolean(formErrors.accTypeCode)}
                helperText={formErrors.accTypeCode}
                placeholder="Select an account type"
                disabled={readOnly}
                showErrorBelow={true}
                searchable={false}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
                dataPrivate={false}
              />
            )}

            {/* Reference Number — hidden from UI; auto-set to user email on save */}

            {/* Debt Type - Selectable card types for the Cards section (UW flow) */}
            {isCardTypeSelectable && (
              <Select
                label="Debt type"
                dialogLabel="Select a card type"
                options={CARD_DEBT_TYPES.map((opt) => ({ value: opt.code, label: opt.label }))}
                value={draft.accTypeCode ?? ''}
                onChange={(value) => handleCardDebtTypeChange(String(value))}
                error={Boolean(formErrors.accTypeCode)}
                helperText={formErrors.accTypeCode}
                placeholder="Select a card type"
                disabled={readOnly}
                showErrorBelow={true}
                searchable={false}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
                dataPrivate={false}
              />
            )}

            {/* Debt Type - Selectable loan types for the Loans section (UW flow) */}
            {isLoanTypeSelectable && (
              <Select
                label="Debt type"
                dialogLabel="Select a loan type"
                options={LOAN_DEBT_TYPES.map((opt) => ({ value: opt.code, label: opt.label }))}
                value={draft.accTypeCode ?? ''}
                onChange={(value) => handleLoanDebtTypeChange(String(value))}
                error={Boolean(formErrors.accTypeCode)}
                helperText={formErrors.accTypeCode}
                placeholder="Select a loan type"
                disabled={readOnly}
                showErrorBelow={true}
                searchable={false}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
                dataPrivate={false}
              />
            )}

            {/* Debt Type - Fixed "Other Non Priority Debt" for the Others section (UW flow) */}
            {isOtherTypeDefault && (
              <Stack spacing={density === 'compact' ? 0.75 : 1.25} sx={{ mt: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px' }}>
                  Debt type
                </Typography>
                <Box
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    backgroundColor: 'white',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', color: '#747474' }}>{OTHER_DEBT_TYPE_LABEL}</Typography>
                </Box>
              </Stack>
            )}

            {/* Debt Type - Read only for locked categories (Credit Cards, Overdrafts) */}
            {category !== 'arrears' && lockedCategoryType && (
              <Stack spacing={density === 'compact' ? 0.75 : 1.25} sx={{ mt: 0 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px' }}>
                  Debt type<span style={{ color: 'red' }}>*</span>
                </Typography>
                <Box
                  sx={{
                    p: 0,
                    borderRadius: 3,
                    backgroundColor: 'white',
                  }}
                >
                  <Typography sx={{ fontSize: '16px', color: '#747474' }}>{lockedCategoryType.label}</Typography>
                </Box>
              </Stack>
            )}

            {/* Arrears Type - Required for arrears category */}
            {!hideAccountType && category === 'arrears' && (
              <Select
                label="Arrears Type"
                dialogLabel="Select an arrears type"
                options={accountTypes.map((rec) => ({ value: rec.code, label: rec.label }))}
                value={draft.accTypeCode ?? ''}
                onChange={(value) => handleAccountTypeChange(String(value))}
                error={Boolean(formErrors.accTypeCode)}
                helperText={formErrors.accTypeCode}
                placeholder="Select arrears type"
                disabled={readOnly || allAccountTypeOptions.length === 0}
                showErrorBelow={true}
                searchable={false}
                fullWidth
                validation={{ required: true }}
                deferValidation={true}
              />
            )}

            {/* Total outstanding balance */}
            <Stack spacing={density === 'compact' ? 0.75 : 1.25}>
              <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px', color: 'primary.main' }}>
                Total outstanding balance
              </Typography>
              <TextWithTooltip
                value={amountOwedInput}
                onChange={(value) => {
                  // Extra safety: disallow negatives even if another input surface ever passes '-'.
                  const cleaned = value.replace(/,/g, '').replace(/-/g, '');
                  amountOwedInputDirtyRef.current = true;
                  setAmountOwedInput(cleaned);
                  if (cleaned.trim() === '' || cleaned === '.') {
                    handleFieldChange('amountOwed', null);
                    return;
                  }
                  const parsed = parseFloat(cleaned);
                  handleFieldChange('amountOwed', Number.isNaN(parsed) ? null : parsed);
                }}
                placeholder=""
                disabled={readOnly}
              />
            </Stack>

            {/* Repayment amount - Question Group */}
            <QuestionGroupForDialog
              label="Repayment amounts"
              showCalculatedIncome={true}
              amountValue={draft.repaymentAmount ?? null}
              frequencyValue={draft.repaymentFrequency ?? null}
            >
              {/* Amount field */}
              <TextWithTooltip
                value={repaymentAmountInput}
                onChange={(value) => {
                  // Extra safety: disallow negatives even if another input surface ever passes '-'.
                  const cleaned = value.replace(/,/g, '').replace(/-/g, '');
                  repaymentAmountInputDirtyRef.current = true;
                  setRepaymentAmountInput(cleaned);
                  if (cleaned.trim() === '') {
                    repaymentClearedRef.current = true;
                    handleFieldChange('repaymentAmount', null);
                    return;
                  }
                  // Intermediate typing value; don't treat as cleared.
                  repaymentClearedRef.current = false;
                  if (cleaned === '.') {
                    handleFieldChange('repaymentAmount', null);
                    return;
                  }
                  const parsed = parseFloat(cleaned);
                  handleFieldChange('repaymentAmount', Number.isNaN(parsed) ? null : parsed);
                }}
                placeholder=""
                disabled={readOnly}
              />

              {/* Frequency field */}
              <Box sx={{ mb: density === 'compact' ? 1 : 2 }}>
                <SelectToggle
                  label="How often?"
                  options={REPAYMENT_OPTIONS.map(opt => ({
                    value: opt.value,
                    label: opt.label
                  }))}
                  value={draft.repaymentFrequency ?? ''}
                  onChange={(value) => handleFieldChange('repaymentFrequency', value as DebtRepaymentFrequencyUi)}
                  disabled={readOnly}
                />
                {formErrors.repaymentFrequency && (
                  <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 0.5 }}>
                    {formErrors.repaymentFrequency}
                  </Typography>
                )}
              </Box>

              {/* Notes field */}
              <CollapsibleTextArea
                label={isOtherTypeDefault || category === 'creditCards' || category === 'loans' || category === 'overdrafts' || category === 'otherDebts' ? 'Notes' : 'Notes (optional)'}
                {...(isOtherTypeDefault
                  ? { subText: 'Please provide a brief description of what this payment is for and any relevant details. Please also tell us if these financial commitments are overdue, behind on payments, or subject to a payment arrangement.' }
                  : (category === 'creditCards' || category === 'loans' || category === 'overdrafts' || category === 'otherDebts')
                    ? { subText: 'Please use the notes field to tell us if these financial commitments are overdue, behind on payments, or subject to a payment arrangement.' }
                    : {})}
                {...(isOtherTypeDefault ? { initiallyExpanded: true, required: true } : {})}
                value={draft.notes ?? ''}
                onChange={(value) => handleFieldChange('notes', value)}
                onKeyDown={(e) => {
                  if (
                    (draft.notes ?? "").length >= 200 &&
                    e.key.length === 1 &&
                    !e.ctrlKey &&
                    !e.metaKey
                  ) {
                    showAlert({
                      severity: "warning",
                      message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                    });
                  }
                }}
                onPaste={(e) => {
                  const maxLength = 200;
                  const pastedText = e.clipboardData.getData("text");
                  const selectionStart = e.target.selectionStart || 0;
                  const selectionEnd = e.target.selectionEnd || 0;
                  const currentLength = (draft.notes ?? "").length;
                  if (
                    currentLength - (selectionEnd - selectionStart) + pastedText.length >
                    maxLength
                  ) {
                    showAlert({
                      severity: "warning",
                      message: UI_MESSAGES.CHARACTER_LIMIT_REACHED,
                    });
                  }
                }}
                maxLength={200}
                placeholder="Add notes..."
                disabled={readOnly}
              />
              {formErrors.notes && (
                <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 0.5 }}>
                  {formErrors.notes}
                </Typography>
              )}
            </QuestionGroupForDialog>

            {/* Payment Offer (conditional) */}
            {!hidePaymentOffer && showPaymentOfferPanel && (
              <Stack spacing={density === 'compact' ? 0.75 : 1.25}>
                <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '16px' }}>
                  Payment offer
                </Typography>
                <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: '14px', mb: 0.5 }}>Amount</Typography>
                    <TextWithTooltip
                      value={paymentOfferAmountInput}
                      onChange={(value) => {
                        const cleaned = value.replace(/,/g, '');
                        if (cleaned.trim() === '') {
                          paymentOfferClearedRef.current = true;
                          paymentOfferInputDirtyRef.current = true;
                          setPaymentOfferAmountInput('');
                          handleFieldChange('paymentOfferAmount', null);
                          return;
                        }
                        paymentOfferClearedRef.current = false;
                        paymentOfferInputDirtyRef.current = true;
                        setPaymentOfferAmountInput(cleaned);
                        // Only skip numeric commit for a lone "." (user just started typing).
                        // Values like "0." or "12." should still count as 0/12 for live calculations.
                        if (cleaned === '.') {
                          return;
                        }
                        const parsed = parseFloat(cleaned);
                        handleFieldChange('paymentOfferAmount', isNaN(parsed) ? null : parsed);
                      }}
                      placeholder=""
                      disabled={readOnly}
                    />
                    {paymentOfferErrorMessage && (
                      <Typography sx={{ fontSize: '12px', color: 'error.main', mt: 0.5 }}>
                        {paymentOfferErrorMessage}
                      </Typography>
                    )}
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Select
                      label="Preferred payment day"
                      dialogLabel="Preferred payment day"
                      options={[...Array.from({ length: 31 }).map((_, i) => ({ value: String(i + 1), label: String(i + 1) }))]}
                      value={draft.paymentOfferMode ? String(draft.paymentOfferMode) : ''}
                      onChange={(val) => handleFieldChange('paymentOfferMode', (val ? Number(val) : null) as any)}
                      disabled={readOnly}
                      placeholder="Please select"
                      showErrorBelow={false}
                      fullWidth
                    />
                  </Box>
                </Stack>
                {typeof availableBudget === 'number' && (
                  <Typography
                    sx={{
                      fontSize: '12px',
                      color: paymentOfferExceedsBudget ? 'error.main' : '#6b7a7b',
                    }}
                  >
                    Amount available in your budget{' '}
                    {formatCurrency((effectiveAvailableBudget ?? availableBudget ?? 0)).replace('££', '£')}
                  </Typography>
                )}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 2,
            gap: 3,
            justifyContent: 'stretch',
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCloseDialog}
            disabled={isSaving}
            sx={{
              borderColor: '#b7b9bc',
              borderRadius: 3,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'none',
              color: 'red',
              display: 'flex',
              gap: 0.5,
              '&:hover': {
                borderColor: '#b7b9bc',
                backgroundColor: 'rgba(255, 0, 0, 0.04)',
              },
              '&:disabled': {
                borderColor: '#b7b9bc',
                color: 'red',
                opacity: 0.5,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={upsertEntry}
            disabled={readOnly || loading || isSaving || paymentOfferExceedsBudget}
            startIcon={isSaving ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              backgroundColor: '#155f54',
              borderRadius: 3,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#0f4a45',
              },
              '&:disabled': {
                backgroundColor: '#155f54',
                opacity: 0.6,
              },
            }}
          >
            {isSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Duplicate detected dialog */}
      <Dialog
        open={duplicateDialogOpen}
        onClose={() => setDuplicateDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Duplicate Company Detected</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {duplicateCompany
              ? `An entry for ${duplicateCompany} with this reference number already exists.`
              : 'An entry with this company and reference number already exists.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => setDuplicateDialogOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* Company-level duplicate dialog (across all debt sections) */}
      <Dialog
        open={companyDuplicateDialogOpen}
        onClose={handleCompanyDuplicateCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Debt already exists</DialogTitle>
        <DialogContent dividers>
          <Typography>
            {pendingCompanySelection
              ? `You already have at least one debt listed for ${pendingCompanySelection}.`
              : 'You already have a debt listed for this company.'}
          </Typography>
          <Typography sx={{ mt: 1.5 }}>
            Are you sure you want to add another debt for this company?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleCompanyDuplicateCancel}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCompanyDuplicateConfirmAdd}>
            Add another debt
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirmation dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
          }
        }}
      >
        <DialogTitle
          component="div"
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 2,
            py: 2,
          }}
        >
          <Typography variant="h6" sx={{ fontSize: '22px', fontWeight: 400, lineHeight: '24px' }}>
            Delete Debt
          </Typography>
          <IconButton
            onClick={handleCancelDelete}
            size="small"
            sx={{
              backgroundColor: '#e0e0e0',
              borderRadius: '50%',
              p: 1,
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers sx={{ px: 2, py: 2 }}>
          <Typography sx={{ fontSize: '16px', lineHeight: '24px' }}>
            Are you sure you want to delete this debt?
          </Typography>
          {entryToDelete && (
            <Box sx={{ mt: 2, p: 1.5, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 600, mb: 0.5 }}>
                {entryToDelete.creditorName || 'Unknown company'}
              </Typography>
              <Typography sx={{ fontSize: '14px', color: '#6b7a7b', mt: 0.5 }}>
                Amount owed: {formatCurrency(entryToDelete.amountOwed)}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            px: 2,
            py: 2,
            gap: 3,
            justifyContent: 'stretch',
          }}
        >
          <Button
            fullWidth
            variant="outlined"
            onClick={handleCancelDelete}
            disabled={deletingEntryId !== null}
            sx={{
              borderColor: '#b7b9bc',
              borderRadius: 3,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'none',
              color: '#003d40',
              '&:hover': {
                borderColor: '#b7b9bc',
                backgroundColor: 'rgba(0, 61, 64, 0.04)',
              },
              '&:disabled': {
                borderColor: '#b7b9bc',
                opacity: 0.5,
              },
            }}
          >
            Cancel
          </Button>
          <Button
            fullWidth
            variant="contained"
            onClick={confirmDeleteDebt}
            disabled={deletingEntryId !== null}
            startIcon={deletingEntryId !== null ? <CircularProgress size={16} color="inherit" /> : undefined}
            sx={{
              backgroundColor: '#d32f2f',
              borderRadius: 3,
              py: 1.5,
              fontSize: '18px',
              fontWeight: 400,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#c62828',
              },
              '&:disabled': {
                backgroundColor: '#d32f2f',
                opacity: 0.6,
              },
            }}
          >
            {deletingEntryId !== null ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DebtCategory;


